import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { documentListQuery } from '@xdocs/schemas';
import type { Document, DocumentListResult, ErrorAlert } from '@xdocs/schemas';
import type { DataTableQuery } from '@xdocs/ui';
import { useWs } from '../../ws/WsProvider.js';

const DEFAULT_QUERY: DataTableQuery = { page: 1, pageSize: 25, sort: 'updatedAt', dir: 'desc' };

export interface UseDocumentsListResult {
  rows: readonly Document[];
  total: number | null;
  /** Synthetic truthy sentinel — `<DataTable>` only checks its truthiness to
   * decide whether to show "Load more"; it never round-trips the value. */
  nextCursor: string | null;
  query: DataTableQuery;
  onQueryChange: (next: DataTableQuery) => void;
  onLoadMore: () => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  loadMoreError: string | undefined;
  onRetry: () => void;
}

/**
 * Server-side pagination/sort/search over `document.list` (F1, E4 §8.3),
 * bridging `<DataTable>`'s desktop-numbered / mobile-cursor-append duality
 * over the same offset-paginated op: desktop `onQueryChange` (sort/search/
 * numbered Prev-Next) REPLACES the row window; mobile `onLoadMore` APPENDS
 * the next page's rows to what's already rendered.
 */
export function useDocumentsList(): UseDocumentsListResult {
  const { wsClient, connectionState } = useWs();
  const [query, setQuery] = useState<DataTableQuery>(DEFAULT_QUERY);
  const [rows, setRows] = useState<Document[]>([]);
  const appendModeRef = useRef(false);
  const enabled = connectionState === 'connected';

  const result = useQuery<DocumentListResult, ErrorAlert>({
    queryKey: ['documents', 'list', query],
    queryFn: () => requestTyped(wsClient, 'document.list', documentListQuery.parse(query)),
    enabled,
  });

  useEffect(() => {
    if (!result.data) return;
    setRows((prev) => (appendModeRef.current ? [...prev, ...result.data.rows] : result.data.rows));
  }, [result.data]);

  const onQueryChange = useCallback((next: DataTableQuery) => {
    appendModeRef.current = false;
    setQuery(next);
  }, []);

  const onLoadMore = useCallback(() => {
    appendModeRef.current = true;
    setQuery((q) => ({ ...q, page: q.page + 1 }));
  }, []);

  const onRetry = useCallback(() => void result.refetch(), [result]);

  const total = result.data?.total ?? null;
  const hasMore = total !== null ? rows.length < total : false;
  const isAppendFetch = appendModeRef.current && result.isFetching;

  return {
    rows,
    total,
    nextCursor: hasMore ? 'more' : null,
    query,
    onQueryChange,
    onLoadMore,
    isLoading: result.isLoading && !appendModeRef.current,
    isLoadingMore: isAppendFetch,
    isError: result.isError,
    loadMoreError: appendModeRef.current && result.isError ? result.error?.message : undefined,
    onRetry,
  };
}
